import { VehicleDetailsVM } from '../../../../../models/orderModel';
import { Component, Input, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  Order,
  OrderBundle,
  OrderBundleRow,
  OrderRow,
  OrderShuttleVM,
  Status,
  OrderCustomer,
  OrderParkingVM,
  KeyTaskVM,
  AirTagVM
} from '../../../../../models/orderModel';
import { AdminService } from '../../../../../services/admin.service';
import { BackOffice24OrderShuttleEditComponent } from '../back-office24-order-shuttle-edit/back-office24-order-shuttle-edit.component';
import { BackOffice24OrderParkingEditComponent } from '../back-office24-order-parking-edit/back-office24-order-parking-edit.component';
import { BackOffice24OrderParkingEditTimeComponent } from '../back-office24-order-parking-edit-time/back-office24-order-parking-edit-time.component';
import { BackOffice24OrderEditComponent } from '../back-office24-order-edit/back-office24-order-edit.component';
import { ConfirmDeleteComponent } from '../../../users/edit-user/confirm-delete/confirm-delete.component';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { BackOffice24OrderAddAdditionalServicesComponent } from '../back-office24-order-add-additional-services/back-office24-order-add-additional-services.component';
import moment from 'moment';
import { BackOffice24OrdersEditAdditionalServiceComponent } from '../back-office24-orders-edit-additional-service/back-office24-orders-edit-additional-service.component';
import { BackOffice24OrdersEditVehicleKeySafeComponent } from '../back-office24-orders-edit-vehicle-key-safe/back-office24-orders-edit-vehicle-key-safe.component';
import { DialogConfirmComponent } from '../../../../../components/dialog-confirm/dialog-confirm.component';
import { MobileNotificationFormComponent } from '../mobile-notification-form/mobile-notification-form.component';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { MyConfigService } from '../../../../../services/my-config.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-back-office24-order-details',
  templateUrl: './back-office24-order-details.component.html',
  styleUrl: './back-office24-order-details.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})

export class BackOffice24OrderDetailsComponent implements OnInit, OnDestroy {
  vehicleKeyStoreTableRows = [];
  displayedKeyStoreColumns = [
    'labelWithNumber',
    'details'
  ];
  vehicleDetails: VehicleDetailsVM;
  isOrderLogTabActive: boolean = false;
  selectedTabIndex: number = 0;
  @Input() organizationId: string;
  @Input() organization: MyConfigOrganization;
  orderId: string;
  editCustomerForm: UntypedFormGroup;
  editMode = false;
  status: Status[] = [
    { value: 'checkout', viewValue: 'Checkout' },
    { value: 'paid', viewValue: 'Betald' },
    { value: 'not-paid', viewValue: 'Obetald' }
  ];
  orderBundle: OrderBundle[];
  orderBundleRows: OrderBundleRow[];
  selectedOrderForm: UntypedFormGroup;
  orderRow: OrderRow;
  orderShuttle: OrderShuttleVM[];
  order: Order;
  orderCustomer: OrderCustomer;
  componentDestroyed$: Subject<boolean> = new Subject();
  orderParkingVM: OrderParkingVM;
  carId: string;

  orderAdditionalServices: any;
  allAdditionalServices = signal<any[]>([]);
  statusOptions: { value: string; name: string }[] = [
    { value: 'Planned', name: 'Planned' },
    { value: 'Finished', name: 'Finished' },
    { value: 'Paid', name: 'Paid' },
  ];
  highestTypeTask: KeyTaskVM;
  airTagList: AirTagVM[] = [];
  orderKeyAirTag: AirTagVM;
  orderKeyTasks: KeyTaskVM[];
  isLoading: boolean = false;
  activeRequests: number = 0;
  private _orderTransactions = new BehaviorSubject<any[]>([]);
  orderTransactions$ = this._orderTransactions.asObservable();
  parkingIsSubscription: boolean = false;
  lprImages: any[] = [];

  constructor(
    public adminService: AdminService,
    private fb: UntypedFormBuilder,
    public snackBar: MatSnackBar,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public dialog: MatDialog,
    public backofficeService: BackOfficeService,
    public myConfigService: MyConfigService,
    public translate: TranslocoService
  ) { }

  get canCreateInvoice(): boolean {
    return this.myConfigService.hasOrganizationIdRole(this.organizationId, 'ParkingAdministrator', this.organization?.organizationParentId)
      || this.myConfigService.hasOrganizationIdRole(this.organizationId, 'ParkingOperator', this.organization?.organizationParentId);
  }

  ngOnInit(): void {
    this.getOrderId();
  }

  onTabChange(event: any) {
    this.selectedTabIndex = event.index;
    this.isOrderLogTabActive = event.index === 2;
  }

  onCustomTabChange(index: number) {
    this.selectedTabIndex = index;
    this.isOrderLogTabActive = index === 2;
  }

  private setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  private incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  private decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  getOrderId() {
    this.activatedRoute.params.pipe(takeUntil(this.componentDestroyed$)).subscribe(params => {
      this.orderId = params['orderId'];
      this.initializeForm();
    });
  }

  initializeForm() {
    this.getOrder(this.orderId);
    this.getOrderBundle(this.orderId);
    this.getOrderRows(this.orderId);
    this.getOrderAdditionalServices(this.orderId);
    this.getOrderVehicleKeyStatus();
  }

  getOrderVehicleKeyStatus() {
    this.incrementRequests();
    this.backofficeService.getOrderKeyTasks(this.orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: orderKeyTasks => {
          this.orderKeyTasks = orderKeyTasks;
          this.setVehicleKeyInfo(orderKeyTasks, this.orderKeyAirTag);
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  getAirTagList(organizationId: string) {
    this.incrementRequests();
    this.backofficeService.getAirtagList(organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: orderAirTagTasks => {
          this.airTagList = orderAirTagTasks;
          this.orderKeyAirTag = orderAirTagTasks.find((airTag: AirTagVM) => airTag.orderId === this.orderId);
          this.setVehicleKeyInfo(this.orderKeyTasks, this.orderKeyAirTag);
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  setVehicleKeyInfo(keyDetails: KeyTaskVM[], airTag?: AirTagVM) {
    this.vehicleKeyStoreTableRows = [];

    if (keyDetails && keyDetails.length > 0) {

      this.highestTypeTask = keyDetails.reduce((prev: any, current: any) => {
        return (prev.type > current.type) ? prev : current;
      });

      if (this.highestTypeTask.statusName !== "Finished") {
        this.vehicleKeyStoreTableRows = [
          {
            labelWithNumber: `Task`,
            details: {
              action: 'Key Task'
            }
          }];
      }

      this.vehicleKeyStoreTableRows = [...this.vehicleKeyStoreTableRows,
      {
        labelWithNumber: `Safe: ${this.highestTypeTask.orderSafe?.hookSafeName}, Hook: ${this.highestTypeTask.orderSafe?.hookNo}`,
        details: {
          status: this.highestTypeTask.orderSafe.status
        }
      }
      ];
    }

    if (airTag) {
      this.vehicleKeyStoreTableRows = [...this.vehicleKeyStoreTableRows,
      {
        labelWithNumber: `AirTag: ${airTag.name}`,
        details: {
          isAirTagStatus: true,
          status: airTag.status
        }
      }
      ];
    }
  }

  takeKeyFromCustomerNoAS() {
    this.incrementRequests();
    this.backofficeService.takeKeyFromCustomerNoAS(this.organizationId, this.orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.getOrderVehicleKeyStatus();
          // this.getOrderAdditionalServices(this.orderId);
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  markKeyTakenFromCustomerDone(orderLockTaskId: string) {
    this.incrementRequests();
    this.backofficeService.markKeyTakenFromCustomerDone(this.organizationId, orderLockTaskId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.getOrderVehicleKeyStatus();
          this.getOrderAdditionalServices(this.orderId);
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  markDriverTaskDone(orderLockTaskId: string) {
    this.incrementRequests();
    this.backofficeService.markDriverTaskDone(this.organizationId, orderLockTaskId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.getOrderVehicleKeyStatus();
          this.getOrderAdditionalServices(this.orderId);
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  getSafeHookStatus(status: number) {
    switch (status) {
      case 0:
        return { name: 'Hook Empty', color: '#FFB300' };
      case 1:
        return { name: 'Key in safe', color: '#4CAF50' };
      case 2:
        return { name: 'Completed', color: '#2196F3' };
      case 3:
        return { name: 'Key with service', color: '#FFB300' };
      default:
        return { name: 'Unknown', color: '#616161' };
    }
    // Hook Status
    // 0 free
    // 1 hook
  }

  getAirTagStatus(status: number) {
    switch (status) {
      case 0:
        return { name: 'Not Attached', color: '#FFB300' };
      case 1:
        return { name: 'Attached', color: '#4CAF50' };
      default:
        return { name: 'Unknown', color: '#616161' };
    }
    // Hook Status
    // 0 free
    // 1 hook
  }

  getOrderVehicleDetails(carId: string, type: string) {
    this.incrementRequests();
    this.backofficeService.getOrderVehicleDetails(carId, type)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: orderVehicleDetails => {
          this.vehicleDetails = orderVehicleDetails;
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  async getVehicleInfo(type: number) {
    try {
      const dialogRef = this.dialog.open(DialogConfirmComponent, {
        width: '400px',
        data: {
          title: type === 2 ? 'VEHICLE_DETAILS.BUTTONS.GET_VEHICLE_INFO' : 'VEHICLE_DETAILS.BUTTONS.RENEW_VEHICLE_INFO',
          message: type === 2 ? 'VEHICLE_DETAILS.CONFIRM.GET_VEHICLE_INFO' : 'VEHICLE_DETAILS.CONFIRM.RENEW_VEHICLE_INFO',
          confirmText: 'FORM.CONFIRM',
          cancelText: 'FORM.CANCEL'
        }
      });

      const result = await dialogRef.afterClosed().toPromise();

      if (result) {
        this.getOrderVehicleDetails(this.carId, type.toString());
      }
    } catch (error) {
      this.snackBar.open("'COMMON.ERROR.GENERAL' | transloco", 'OK', {
        duration: 2000,
        verticalPosition: 'top'
      });
    }
  }

  orderPaymentCapture(orderId: string) {
    this.incrementRequests();
    this.backofficeService
      .orderPaymentCapture(orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: order => {
          this.snackBar.open(`Order ${this.order.orderNr} Captured`, 'OK', {
            duration: 2000,
            verticalPosition: 'top'
          });
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  orderPaymentReversal(orderId: string) {
    this.incrementRequests();
    this.backofficeService
      .orderPaymentReversal(orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: order => {
          this.snackBar.open(`Order ${this.order.orderNr} Capture Reversal`, 'OK', {
            duration: 2000,
            verticalPosition: 'top'
          });
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  setOrderstatusCancel(orderId: string) {
    const repayMoneyText = this.translate.translate('FORM.REPAY_FEE') || 'Återbetala Avgift';
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill ändra status till Cancel?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        },
        showRepayMoney: true,
        repayMoneyText: repayMoneyText
      }
    });
    dialogRef.afterClosed().subscribe((res: any) => {
      if (res) {
        const repayMoney = typeof res === 'object' && res !== null ? !!res.repayMoney : false;
        this.incrementRequests();
        this.backofficeService
          .setOrderstatusCancel(orderId, repayMoney)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: orderVM => {
              this.snackBar.open(`Du har cancellerat order: ${this.order.orderNr}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
              window.location.reload();
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  setOrderstatusPaid(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill återställa ordern?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .setOrderStatusPaid(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: orderVM => {
              this.snackBar.open(`Du har återställt order: ${this.order.orderNr}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
              window.location.reload();
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendOrderConfirmation(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka orderbekräftelse?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendOrderConfirmation(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat bekräftelse mail till: ${this.order.customer.email}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendOrderAdditionalPayment(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka tilläggsbetalning?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendOrderAdditionalPayment(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat tilläggsbetalning till: ${this.order.customer.email}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendDebtReminder(orderId: string, reminderNr: number) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka påminnelse ${reminderNr}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendDebtReminder(orderId, reminderNr)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat påminnelse ${reminderNr} till: ${this.order.customer.email}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
              this.getOrder(orderId);
            },
            error: error => {
              this.snackBar.open(`Fel vid skickande av påminnelse ${reminderNr}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
              this.decrementRequests();
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendVingOrderConfirmation(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka ving order bekräftelse?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendVingOrderConfirmation(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat ving order bekräftelse till: ${this.order.customer.email}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendOrderAdditionalServiceOffer(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka tilläggstjänst erbjudande?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendOrderAdditionalServiceOffer(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat tilläggstjänst erbjudande till: ${this.order.customer.email}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendParkingLPRCheckedInSMS(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka checkin SMS?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendParkingLPRCheckedInSMS(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat checkin SMS till: ${this.order.customer.cellular}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendSMSTerminal2(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka Termianl 2 SMS?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendOrderSMS('Terminal2', orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat Terminal 2 SMS till: ${this.order.customer.cellular}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendSMSTerminal4(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka Terminal 4 SMS?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendOrderSMS('Terminal4', orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat Terminal 4 SMS till: ${this.order.customer.cellular}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendOrderParkingCheckinConfirmation(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka parkeringansvisning?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendOrderParkingCheckinConfirmation(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat parkeringansvisnings mail till: ${this.order.customer.email}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendOrderWelcomeHome(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka välkommen hem mail?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendOrderWelcomeHome(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat välkommen hem mail till: ${this.order.customer.email}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  sendOrderCancel(orderId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill skicka avbokningsmail?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .sendOrdeCancel(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har skickat avbokningsmail till: ${this.order.customer.email}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  getOrder(orderId: string) {
    this.incrementRequests();
    this.backofficeService
      .getOrder(orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: order => {
          this.order = order;
          if (order?.images && order.images.length > 0) {
            this.lprImages = order?.images;
          }

          if (order?.transactions) {
            this._orderTransactions.next([...order?.transactions]);
          }

          this.getOrganizationId();
          this.getAllOrderAdditionalServices();
          this.getAirTagList(this.order.organizationId);
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  // reminders go in order: N is available only after N-1 was sent
  canSendDebtReminder(reminderNr: number): boolean {
    const sent = [
      true,
      !!this.order?.debtReminders?.debtReminder1Sent,
      !!this.order?.debtReminders?.debtReminder2Sent,
      !!this.order?.debtReminders?.debtReminder3Sent
    ];
    return sent[reminderNr - 1] && !sent[reminderNr];
  }

  getOrderAdditionalServices(orderId: string) {
    this.incrementRequests();
    this.backofficeService
      .getOrderAdditionalServices(orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: order => {
          this.orderAdditionalServices = order;
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  getAllOrderAdditionalServices() {
    this.backofficeService
      .getOrganizationAdditionalServices(this.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: additionalServices => {
          this.allAdditionalServices.set(additionalServices);
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  getOrderBundle(orderId: string) {
    this.incrementRequests();
    this.backofficeService
      .getOrderBundle(orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: orderBundle => {
          if (orderBundle.length > 0) {
            this.orderBundle = orderBundle;
            this.orderBundleRows = orderBundle[0].orderBundleRows;

            this.orderBundleRows.forEach(orderBundleRow => {
              if (orderBundleRow.orderParkingVM != null) {
                this.orderParkingVM = orderBundleRow.orderParkingVM;
                console.log('orderBundleRow.orderParkingVM', orderBundleRow.orderParkingVM);
              }
            });

            this.parkingIsSubscription = this.orderParkingVM?.parkingProductType === 'Subscription';

            if (this.findCarId(this.orderBundleRows, null)) {
              this.carId = this.findCarId(this.orderBundleRows, null);
              this.getOrderVehicleDetails(this.carId, '1');
            }
          }
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  getOrderRows(orderId: string) {
    this.incrementRequests();
    this.backofficeService
      .getOrderRows(orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: orderRows => {
          if (orderRows) {
            this.orderRow = orderRows;
            this.orderShuttle = orderRows.orderShuttles;

            this.orderRow.orderParkings.forEach(orderParking => {
              this.orderParkingVM = orderParking;
            });
            this.parkingIsSubscription = this.orderParkingVM?.parkingProductType === 'Subscription';

            if (this.findCarId(null, this.orderRow)) {
              this.carId = this.findCarId(null, this.orderRow);
              this.getOrderVehicleDetails(this.carId, '1');
            }
          }
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  getOrganizationId() {
    this.organizationId = this.order.organizationId;
    this.organization = this.myConfigService.getMyConfigOrganizationById(this.organizationId);
  }

  getOrderHotel(orderId: string) {
    this.incrementRequests();
    this.backofficeService
      .getOrderHotel(orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: hotelOrder => {
        },
        complete: () => {
          this.decrementRequests();
        }
      });
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  editOrderCustomer(orderId: string): void {
    const dialogRef = this.dialog.open(BackOffice24OrderEditComponent, {
      // height: '1010px',
      width: '720px',
      hasBackdrop: true,
      data: { 'orderId': orderId, 'showAdress': true }
    });
    dialogRef.afterOpened().subscribe(() => {
      const componentInstance = dialogRef.componentInstance as BackOffice24OrderEditComponent;
      componentInstance.saveChangesEvent.subscribe(() => {
        this.initializeForm();
      });
    });
  }

  editOrderShuttle(orderShuttleId: string): void {
    const dialogRef = this.dialog.open(BackOffice24OrderShuttleEditComponent, {
      // height: '1010px',
      width: '720px',
      hasBackdrop: true,
      data: { "orderShuttleId": orderShuttleId }
    });
    dialogRef.afterOpened().subscribe(() => {
      const componentInstance = dialogRef.componentInstance as BackOffice24OrderShuttleEditComponent;
      componentInstance.saveChangesEvent.subscribe(() => {
        this.initializeForm();
      });
    });
  }

  editOrderParking(orderParkingId: string): void {
    const dialogRef = this.dialog.open(BackOffice24OrderParkingEditComponent, {
      // height: '1010px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      hasBackdrop: true,
      data: {
        orderParkingId: orderParkingId,
        isSubscription: this.parkingIsSubscription
      }
    });

    dialogRef.afterOpened().subscribe(() => {
      const componentInstance = dialogRef.componentInstance as BackOffice24OrderParkingEditComponent;
      componentInstance.saveChangesEvent.subscribe(() => {
        this.initializeForm();
      });
    });
  }

  editOrderParkingTime(orderParkingId: string): void {
    const dialogRef = this.dialog.open(BackOffice24OrderParkingEditTimeComponent, {
      // height: '1010px',
      width: '720px',
      hasBackdrop: true,
      data: orderParkingId
    });

    dialogRef.afterOpened().subscribe(() => {
      const componentInstance = dialogRef.componentInstance as BackOffice24OrderParkingEditTimeComponent;
      componentInstance.saveChangesEvent.subscribe(() => {
        this.initializeForm();
      });
    });
  }

  addOrderAdditionalService(): void {
    const dialogRef = this.dialog.open(BackOffice24OrderAddAdditionalServicesComponent, {
      // height: '1010px',
      width: '720px',
      hasBackdrop: true,
      data: {
        orderId: this.order.orderId,
        orderAdditionalServices: this.orderAdditionalServices,
        allAdditionalServices: this.allAdditionalServices()
      }
    });
    dialogRef.afterOpened().subscribe(() => {
      const componentInstance = dialogRef.componentInstance as BackOffice24OrderAddAdditionalServicesComponent;
      componentInstance.saveChangesEvent.subscribe(() => {
        this.initializeForm();
      });
    });
  }

  editOrderAdditionalService(service: any): void {
    // Find the service template ID from allAdditionalServices
    const serviceTemplate = this.allAdditionalServices().find(template =>
      template.name === service.name || template.id === service.additionalServiceId
    );

    const dialogRef = this.dialog.open(BackOffice24OrdersEditAdditionalServiceComponent, {
      width: '720px',
      hasBackdrop: true,
      data: {
        additionalService: service,
        parkingFrom: this.orderParkingVM?.parkingFrom,
        parkingTill: this.orderParkingVM?.parkingTo,
        organization: {
          organizationId: this.organizationId
        },
        serviceTemplateId: serviceTemplate?.id
      }
    });
    dialogRef.afterOpened().subscribe(() => {
      const componentInstance = dialogRef.componentInstance as BackOffice24OrdersEditAdditionalServiceComponent;
      componentInstance.saveChangesEvent.subscribe((editedAdditionalService) => {
        this.getOrderAdditionalServices(this.orderId);
        this.getOrderVehicleKeyStatus();
      });
    });
  }

  updateOrderAdditionalServices(additionalService) {
    const currentServices = this.orderAdditionalServices;
    const index = currentServices.findIndex(dc => dc.id === additionalService.id);
    if (index > -1) {
      const updatedServices = [...currentServices];
      updatedServices[index] = additionalService;
      this.orderAdditionalServices = updatedServices;
    }
    else {
      this.orderAdditionalServices = [additionalService, ...currentServices];
    }
  }

  editOrderVehicleKeyDetails(): void {
    const dialogRef = this.dialog.open(BackOffice24OrdersEditVehicleKeySafeComponent, {
      width: '720px',
      hasBackdrop: true,
      data: {
        organizationId: this.organizationId,
        orderId: this.order.orderId,
        airTagList: this.airTagList.filter((airTag: AirTagVM) => airTag.status === 0 || airTag.id === this.orderKeyAirTag?.id),
        currentAirTag: this.orderKeyAirTag,
        additionalServices: this.orderAdditionalServices.filter(
          (service: any) =>
            (service.status !== 'Underway' && service.status !== 'Completed' && service.status !== 'Cancelled' && service.status !== 'Removed')
            || (service.id === this.orderKeyAirTag?.orderAdditionServiceId)
        ),
      }
    });
    dialogRef.afterOpened().subscribe(() => {
      const componentInstance = dialogRef.componentInstance as BackOffice24OrdersEditVehicleKeySafeComponent;
      componentInstance.saveChangesEvent.subscribe(async () => {
        await this.getOrderVehicleKeyStatus();
        await this.getAirTagList(this.organizationId);
        await this.getOrderAdditionalServices(this.orderId);
      });
    });
  }
  gotoCustomerPage(email, bookingNumber) {
    if (!email || !bookingNumber) {
      console.error('Email or booking number is missing');
      return;
    }
    const url = `/parkifly/booking-overview;email=${email};booking_number=${bookingNumber}`;
    window.open(url, '_blank');
  }

  createInvoice(orderId) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill gör invoice?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.incrementRequests();
        this.backofficeService
          .createInvoice(orderId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: status => {
              this.snackBar.open(`Du har görde invoice`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
              this.getOrder(orderId);
            },
            error: error => {
              this.snackBar.open(`Fel vid skapande av invoice}`, 'OK', {
                duration: 2000,
                verticalPosition: 'top'
              });
            },
            complete: () => {
              this.decrementRequests();
            }
          });
      }
    });
  }

  statusColor(status: string) {
    switch (status) {
      case 'Planned':
        return 'primary';
      case 'Finished':
        return 'accent';
      case 'Paid':
        return 'warn';
      case 'Cancelled':
        return 'warn';
      case 'In':
        return 'green';
      case 'Out':
        return 'red';
      default:
        return 'primary';
    }
  }

  getCustomerInformation(orderCustomer: OrderCustomer) {
    return [
      { label: 'CUSTOMER.NAME', value: orderCustomer.firstName + " " + orderCustomer.lastName },
      { label: 'CUSTOMER.EMAIL', value: orderCustomer.email },
      { label: 'CUSTOMER.PHONE', value: orderCustomer.cellular },
      {
        label: 'ORDER_DETAILS.ORDER_INFO.COMMENT',
        value: this.order.comment,
        isStatusBadge: false
      },
      // {
      //   label: 'ORDER_DETAILS.ORDER_INFO.ORGANIZATION',
      //   value: order.organizationName,
      //   isStatusBadge: false
      // },
      // ...(this.order.organizationName === 'Skavsta'
      //   ?
      //   [
      //     {
      //       label: 'ORDER_DETAILS.ORDER_INFO.EXTERNAL_ID',
      //       value: this.order.externalId ? this.order.externalId : 'ORDER_DETAILS.ORDER_INFO.MISSING_ORDER_AT_SKAVSTA',
      //       isStatusBadge: false
      //     }
      //   ] : [])

    ];
  }

  getOrderInformation(order, orderBundle) {
    return [
      ...(order.paymentStatus !== null ?
        [
          {
            label: 'ORDER_DETAILS.ORDER_INFO.PAYMENT_STATUS',
            value: order?.paymentStatus,
            isStatusBadge: true,
            isPayment: true
          }
        ] : []),
      ...(order.paymentType !== null ?
        [
          {
            label: 'ORDER_DETAILS.ORDER_INFO.METHOD',
            value: order.paymentType,
            isStatusBadge: false,
          }
        ] : []),
      {
        label: 'ORDER_DETAILS.ORDER_INFO.AMOUNT',
        value: order.totalDiscountPriceIncVat != null ? `${order.totalDiscountPriceIncVat} Kr` : `${order.totalPriceIncVat} Kr`,
        isStatusBadge: false
      },
      {
        label: 'ORDER_DETAILS.ORDER_INFO.DESCRIPTION',
        value: orderBundle && orderBundle.length > 0 ? orderBundle[0].description : '',
        isStatusBadge: false
      },
      {
        label: 'ORDER_DETAILS.ORDER_INFO.COMMENT',
        value: order.comment,
        isStatusBadge: false
      },
      // {
      //   label: 'ORDER_DETAILS.ORDER_INFO.ORGANIZATION',
      //   value: order.organizationName,
      //   isStatusBadge: false
      // },
      // ...(order.organizationName === 'Skavsta'
      //   ?
      //   [
      //     {
      //       label: 'ORDER_DETAILS.ORDER_INFO.EXTERNAL_ID',
      //       value: order.externalId ? order.externalId : 'ORDER_DETAILS.ORDER_INFO.MISSING_ORDER_AT_SKAVSTA',
      //       isStatusBadge: false
      //     }
      //   ] : [])
    ];
  }

  getShuttleDetailsData1(bundle: any) {
    return [
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.STATUS',
        value: bundle.status,
        isStatusBadge: true,
        isPayment: true
      },
      {
        label: 'ORDER_DETAILS.SHUTTLE_SERVICE.FLIGHT_NUMBER',
        value: bundle.flightNr
      },
      ...this.getShuttleFlightDetails(bundle),
      {
        label: 'ORDER_DETAILS.SHUTTLE_SERVICE.NUMBER_OF_PERSONS',
        value: bundle.passengers
      },
      {
        label: 'ORDER_DETAILS.SHUTTLE_SERVICE.DATE',
        value: bundle.shuttleDate
      },
      {
        label: 'ORDER_DETAILS.SHUTTLE_SERVICE.COMMENT',
        value: bundle.comment
      }
    ];
  }

  getShuttleDetailsData2(bundle: any) {
    return [
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.STATUS',
        value: bundle.status,
        isStatusBadge: true,
        isPayment: true
      },
      {
        label: 'ORDER_DETAILS.SHUTTLE_SERVICE.FLIGHT_NUMBER',
        value: bundle.flightNr
      },
      ...this.getShuttleFlightDetails(bundle),
      {
        label: 'ORDER_DETAILS.SHUTTLE_SERVICE.NUMBER_OF_PERSONS',
        value: bundle.passengers
      },
      {
        label: 'ORDER_DETAILS.SHUTTLE_SERVICE.DATE',
        value: bundle.shuttleDate
      },
      {
        label: 'ORDER_DETAILS.SHUTTLE_SERVICE.COMMENT',
        value: bundle.comment
      }
    ];
  }

  private getShuttleFlightDetails(bundle: any) {
    const flightVM = bundle.productType === 'ShuttleDeparture'
      ? bundle.departureVM
      : bundle.arrivalVM;

    return flightVM ? [
      {
        label: bundle.productType === 'ShuttleDeparture'
          ? 'ORDER_DETAILS.SHUTTLE_SERVICE.DEPARTING_TO'
          : 'ORDER_DETAILS.SHUTTLE_SERVICE.ARRIVING_FROM',
        value: bundle.productType === 'ShuttleDeparture'
          ? flightVM.arrivalStation
          : flightVM.departureStation
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.STATUS',
        value: flightVM.status
      }
    ] : [];
  }

  getBundleParkingDetailsData(orderParkingVM: any) {
    return [
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.STATUS',
        value: orderParkingVM?.status,
        isStatusBadge: true,
        isPayment: true
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.REGISTRATION_NUMBER',
        value: orderParkingVM?.carId
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.LOCATION',
        value: orderParkingVM?.parkingSiteName
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.PARKING_FROM',
        value: orderParkingVM?.parkingFrom
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.PARKING_TO',
        value: orderParkingVM?.parkingTo
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.CHECK_IN',
        value: orderParkingVM?.checkIn
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.CHECK_OUT',
        value: orderParkingVM?.checkOut
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.COMMENT',
        value: orderParkingVM?.comment
      }
    ];
  }

  getParkingDetailsData(parking: any) {
    return [
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.STATUS',
        value: parking.status,
        isStatusBadge: true,
        isPayment: true
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.REGISTRATION_NUMBER',
        value: parking.carId
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.LOCATION',
        value: parking.parkingSiteName
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.TYPE',
        value: parking.parkingProductType
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.PARKING_FROM',
        value: parking.parkingFrom
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.PARKING_TO',
        value: parking.parkingTo
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.CHECK_IN',
        value: parking.checkIn
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.CHECK_OUT',
        value: parking.checkOut
      },
      {
        label: 'ORDER_DETAILS.PARKING_SERVICE.COMMENT',
        value: parking.comment
      }
    ];
  }

  getAdditionalServiceStatusColor(status: string): string {
    switch (status) {
      case 'New':
        return '#3f51b5';
      case 'Paid':
        return '#28a745';
      case 'Planned':
        return '#17a2b8';
      case 'Underway':
        return '#ffc107';
      case 'Completed':
        return '#6c757d';
      case 'Cancelled':
        return '#dc3545';
      case 'Removed':
        return '#F44336';
      default:
        return '#adb5bd';
    }
  }

  findCarId(orderBundleRows: OrderBundleRow[], orderRow: OrderRow): string | null {
    let result: string | null = null;
    if (orderBundleRows && orderBundleRows.length > 0) {
      orderBundleRows.forEach(orderBundleRow => {
        if (orderBundleRow.orderParkingVM && orderBundleRow.orderParkingVM?.carId) {
          result = orderBundleRow.orderParkingVM?.carId;
        }
      });
    }

    if (orderRow && orderRow.orderParkings && orderRow.orderParkings.length > 0) {
      orderRow.orderParkings.forEach(orderParking => {
        if (orderParking.carId) {
          result = orderParking.carId;
        }
      });
    }

    return result;
  }

  getVehicleInformation(vehicleDetails: VehicleDetailsVM) {
    const ownerType = vehicleDetails?.owner?.data?.type;
    const personInfo = vehicleDetails?.owner?.data?.parsedPerson;
    const companyInfo = vehicleDetails?.owner?.data?.parsedCompany;

    const ownerFields = ownerType === 'person' ? [
      {
        label: 'VEHICLE_DETAILS.OWNER_NAME',
        value: personInfo?.name?.full_name || `${personInfo?.name?.first_name || ''} ${personInfo?.name?.last_name || ''}`.trim()
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_ADDRESS',
        value: personInfo?.address?.name
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_CITY',
        value: personInfo?.address?.city
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_ZIP_CODE',
        value: personInfo?.address?.zip_code
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_COUNTRY',
        value: personInfo?.address?.county
      }
    ] : [
      {
        label: 'VEHICLE_DETAILS.OWNER_NAME',
        value: companyInfo?.name?.legal_name,
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_ADDRESS',
        value: companyInfo?.address?.name
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_CITY',
        value: companyInfo?.address?.city
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_ZIP_CODE',
        value: companyInfo?.address?.zip_code
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_COUNTRY',
        value: companyInfo?.address?.county
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_ORG_NUMBER',
        value: companyInfo?.orgnr
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_STATUS',
        value: companyInfo?.status
      }
    ];

    return [
      // Registration Info
      {
        label: 'VEHICLE_DETAILS.REGISTRATION_NUMBER',
        value: vehicleDetails?.attributes?.regno
      },

      // Basic Info
      {
        label: 'VEHICLE_DETAILS.MAKE',
        value: vehicleDetails?.basic?.data?.make
      },
      {
        label: 'VEHICLE_DETAILS.MODEL',
        value: vehicleDetails?.basic?.data?.model
      },
      {
        label: 'VEHICLE_DETAILS.COLOR',
        value: vehicleDetails?.basic?.data?.color
      },
      {
        label: 'VEHICLE_DETAILS.VEHICLE_TYPE',
        value: this.getVehicleType(vehicleDetails?.basic?.data?.type)
      },
      {
        label: 'VEHICLE_DETAILS.VEHICLE_YEAR',
        value: vehicleDetails?.basic?.data?.vehicle_year
      },
      {
        label: 'VEHICLE_DETAILS.MODEL_YEAR',
        value: vehicleDetails?.basic?.data?.model_year
      },
      {
        label: 'VEHICLE_DETAILS.OWNER_TYPE',
        value: vehicleDetails?.owner?.data?.type
      },

      // Owner Info
      ...ownerFields,

      {
        label: 'VEHICLE_DETAILS.INSPECTION_EXPIRES_AT',
        value: vehicleDetails?.basic?.data?.inspection_valid_until
          ? moment(vehicleDetails.basic.data.inspection_valid_until).format('YYYY-MM-DD')
          : ''
      },
      {
        label: 'VEHICLE_DETAILS.VEHICLE_STATUS',
        value: this.getVehicleStatus(vehicleDetails?.basic?.data?.status)
      },
      {
        label: 'VEHICLE_DETAILS.CACHED_AT',
        value: vehicleDetails?.cashedAt ? moment(vehicleDetails.cashedAt).format('YYYY-MM-DD, HH:mm:ss') : ''
      },
    ];
  }

  getVehicleStatus(status: any) {
    switch (status) {
      case '1':
        return 'Avställd';
      case '2':
        return 'I trafik';
      case '3':
        return 'Avregistrerad';
      case '4':
        return 'Aldrig i trafik';
      default:
        return status;
    }
  }

  getVehicleType(type: any) {
    switch (type) {
      case 'BUSS':
        return 'Buss';
      case 'LB':
        return 'Lastbil';
      case 'MC':
        return 'Motorcykel';
      case 'MOPED':
        return 'Moped';
      case 'MRED':
        return 'Motorredskap';
      case 'PB':
        return 'Personbil';
      case 'SLÄP':
        return 'Släp';
      case 'TGHJUL':
        return 'Terränghjuling';
      case 'TGSK':
        return 'Terrängskoter';
      case 'TGSL':
        return 'Terrängsläp';
      case 'TGSNÖ':
        return 'Snöskoter';
      case 'TGV':
        return 'Terrängvagn';
      case 'TR':
        return 'Traktor';
      default:
        return type;
    }
  }

  get sortedOrderBundleRows(): OrderBundleRow[] {
    if (!this.orderBundleRows) return [];
    return [...this.orderBundleRows].sort((a, b) => {
      const aIsDeparture = a.orderShuttleVM?.productType === 'ShuttleDeparture' ? 0 : 1;
      const bIsDeparture = b.orderShuttleVM?.productType === 'ShuttleDeparture' ? 0 : 1;
      return aIsDeparture - bIsDeparture;
    });
  }

  get sortedOrderShuttles(): any[] {
    if (!this.orderRow?.orderShuttles) return [];
    return [...this.orderRow.orderShuttles].sort((a, b) => {
      const aIsDeparture = a.productType === 'ShuttleDeparture' ? 0 : 1;
      const bIsDeparture = b.productType === 'ShuttleDeparture' ? 0 : 1;
      return aIsDeparture - bIsDeparture;
    });
  }

  get shouldShowAirTagIcon(): boolean {
    if (!this.highestTypeTask) return false;

    const hasValidServices = this.orderAdditionalServices?.some((service: any) =>
      (service.status !== 'Underway' &&
        service.status !== 'Completed' &&
        service.status !== 'Cancelled' &&
        service.status !== 'Removed') ||
      (this.orderKeyAirTag?.orderAdditionServiceId === service.id)
    );

    return this.highestTypeTask.type === 1 &&
      this.highestTypeTask.statusName === 'Finished' &&
      hasValidServices;
  }

  openNotificationForm(orderId: string) {
    this.dialog.open(MobileNotificationFormComponent, {
      data: {
        orderId: orderId,
        title: '',
        message: '',
        payload: ''
      }
    });
  }

  copyToClipboard(label: string, value: string) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = value;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    let content = 'Content copied to clipboard';
    if (label == 'ORDER_DETAILS.PARKING_SERVICE.REGISTRATION_NUMBER') {
      const message = this.translate.translate('FORM.COPY_REG_NR');
      content = message;
    }
    if (label == 'CUSTOMER.EMAIL') {
      const message = this.translate.translate('FORM.COPY_EMAIL');
      content = message;
    }
    this.snackBar.open(content, 'OK', {
      duration: 2000,
      verticalPosition: 'top'
    });
  }
}
